import { createEffect, sample } from 'effector'

import type { PublicProfileGameInfo } from '@portal/portal-api/profile'

import { analytics } from '@/shared/lib/analytics'
import { gtm } from '@/shared/lib/google-tag-manager'

type LoginEvent = {
    event: 'login'
    user_id: string
    user_email: string
    user_new: boolean
    time_since_last_login?: number
}

type PurchaseEvent = {
    event: 'purchase'
    purchase_value_eur: string
    purchase_value_usd: string
    transaction_id: string
    first_purchase: boolean
}

type LevelUpEvent = {
    event: 'levelup'
    level: number
}

type RegistrationCompleteEvent = {
    event: 'registrationComplete'
    registration_id: string
}

type GTMEvent = LoginEvent | LevelUpEvent | RegistrationCompleteEvent | PurchaseEvent

const gtmEventSendFx = createEffect<GTMEvent, void>((event) => {
    gtm.push(event)
})

sample({
    clock: analytics.login,
    fn: (params): LoginEvent => ({
        event: 'login',
        user_id: params.publicId,
        user_email: params.email,
        user_new: params.isNewAccountGTM ?? false,
        time_since_last_login: calcTimeSinceLastLogin(params.gameInfo),
    }),
    target: gtmEventSendFx,
})

sample({
    clock: analytics.registration,
    fn: (params): RegistrationCompleteEvent => ({
        event: 'registrationComplete',
        registration_id: params.supportId,
    }),
    target: gtmEventSendFx,
})

sample({
    clock: analytics.levelup,
    fn: (params): LevelUpEvent => ({
        event: 'levelup',
        level: params.level,
    }),
    target: gtmEventSendFx,
})

sample({
    clock: analytics.purchase,
    fn: (params): GTMEvent => ({
        event: 'purchase',
        transaction_id: params.transactionId,
        purchase_value_eur: params.grossEUR,
        purchase_value_usd: params.grossUSD,
        first_purchase: params.firstPurchase,
    }),
    target: gtmEventSendFx,
})

function calcTimeSinceLastLogin(gameInfo?: PublicProfileGameInfo): number | undefined {
    if (!gameInfo) {
        return undefined
    }

    if (!gameInfo.loginTime) {
        return undefined
    }

    const now = Date.now()
    const lastLogin = gameInfo.loginTime * 1000
    return Math.floor((now - lastLogin) / 1000)
}
